import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, X, ArrowRight, MessageCircleQuestion, Send, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";
import { track, submitLead } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POPUP_KEY = "alphalane_webinar_popup_dismissed_at";
const REDISPLAY_AFTER_MS = 24 * 60 * 60 * 1000; // 24h Cool-off nach Schliessen

const useNextWebinar = () => {
  const [webinar, setWebinar] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND_URL}/api/webinars/next`)
      .then((r) => r.json())
      .then((d) => { if (alive) setWebinar(d?.webinar || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return webinar;
};

const fmtWebinarDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString(lang === "en" ? "en-GB" : "de-CH", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
    });
    const time = d.toLocaleTimeString(lang === "en" ? "en-GB" : "de-CH", {
      hour: "2-digit", minute: "2-digit",
    });
    return lang === "en" ? `${date} · ${time}` : `${date} · ${time} Uhr`;
  } catch {
    return "";
  }
};

const TXT = {
  de: {
    bannerTag: "Nächstes Live-Webinar",
    bannerCta: "Jetzt Platz sichern",
    popupTitle: "Live-Webinar",
    tabWebinar: "Webinar",
    tabQa: "Q&A",
    popupCta: "Kostenlos anmelden",
    bubbleLabel: "Webinar & Q&A",
    chatIntro: "Hallo! Ich bin der Alphalane-Assistent. Frag mich alles über unser KI-Trading — oder wähle eine Beispielfrage:",
    chatPlaceholder: "Deine Frage…",
    chatError: "Gerade nicht erreichbar — bitte nochmal versuchen.",
    examples: [
      "Wie funktioniert Alphalane?",
      "Was kostet es?",
      "Ist mein Geld sicher?",
      "Kann ich jederzeit auszahlen?",
    ],
    secTag: "Live-Webinar · gratis",
    secTitle1: "Erlebe die Alphalane AI",
    secTitle2: "live in Aktion.",
    secBullets: ["Live-Demo der Alphalane AI", "Track-Record & echte Zahlen", "Offene Q&A mit den Gründern"],
    secName: "Dein Name",
    secEmail: "Deine E-Mail",
    secPhone: "Telefon (optional)",
    secSubmit: "Platz sichern",
    secDone: "Du bist dabei! Alle Details kommen per E-Mail.",
    secErr: "Etwas ging schief — bitte nochmal versuchen.",
    secNameReq: "Bitte gib deinen Namen an.",
  },
  en: {
    bannerTag: "Next live webinar",
    bannerCta: "Reserve your seat",
    popupTitle: "Live webinar",
    tabWebinar: "Webinar",
    tabQa: "Q&A",
    popupCta: "Register for free",
    bubbleLabel: "Webinar & Q&A",
    chatIntro: "Hi! I'm the Alphalane assistant. Ask me anything about our AI trading — or pick an example question:",
    chatPlaceholder: "Your question…",
    chatError: "Currently unavailable — please try again.",
    examples: [
      "How does Alphalane work?",
      "What does it cost?",
      "Is my money safe?",
      "Can I withdraw anytime?",
    ],
    secTag: "Live webinar · free",
    secTitle1: "Experience the Alphalane AI",
    secTitle2: "live in action.",
    secBullets: ["Live demo of the Alphalane AI", "Track record & real numbers", "Open Q&A with the founders"],
    secName: "Your name",
    secEmail: "Your email",
    secPhone: "Phone (optional)",
    secSubmit: "Reserve my seat",
    secDone: "You're in! All details are on their way via email.",
    secErr: "Something went wrong — please try again.",
    secNameReq: "Please enter your name.",
  },
};

const getChatSessionId = () => {
  try {
    let id = localStorage.getItem("alphalane_qa_session");
    if (!id) {
      id = `qa-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("alphalane_qa_session", id);
    }
    return id;
  } catch {
    return `qa-${Date.now()}`;
  }
};

/** KI-Chat im Q&A-Tab: Beispielfragen + freie Eingabe. */
const QaChat = ({ x }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    track("qa_chat_message");
    try {
      const r = await fetch(`${BACKEND_URL}/api/qa-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getChatSessionId(), message: msg }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "error");
      setMessages((m) => [...m, { role: "assistant", text: d.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: x.chatError }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col" data-testid="webinar-popup-qa-tab">
      <div ref={scrollRef} className="max-h-64 overflow-y-auto px-3 pt-3 space-y-2">
        <div className="flex items-start gap-2">
          <div className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: "var(--al-grad)" }}>
            <Sparkles className="h-3 w-3" />
          </div>
          <p className="text-xs text-slate-600 leading-relaxed bg-slate-50 rounded-xl rounded-tl-sm px-3 py-2">{x.chatIntro}</p>
        </div>
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-1.5 pl-8" data-testid="qa-chat-examples">
            {x.examples.map((q, i) => (
              <button
                key={q}
                type="button"
                onClick={() => send(q)}
                data-testid={`qa-chat-example-${i}`}
                className="px-2.5 py-1.5 rounded-full border border-violet-200 bg-violet-50 hover:bg-violet-100 text-[11px] font-semibold text-violet-700 text-left transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "items-start gap-2"}`}>
            {m.role === "assistant" && (
              <div className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: "var(--al-grad)" }}>
                <Sparkles className="h-3 w-3" />
              </div>
            )}
            <p
              data-testid={`qa-chat-msg-${i}`}
              className={`text-xs leading-relaxed px-3 py-2 rounded-xl max-w-[85%] whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-violet-600 text-white rounded-br-sm"
                  : "bg-slate-50 text-slate-700 rounded-tl-sm"
              }`}
            >
              {m.text}
            </p>
          </div>
        ))}
        {loading && (
          <div className="flex items-start gap-2" data-testid="qa-chat-loading">
            <div className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 text-white" style={{ background: "var(--al-grad)" }}>
              <Sparkles className="h-3 w-3" />
            </div>
            <div className="bg-slate-50 rounded-xl rounded-tl-sm px-3 py-2 flex gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" />
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="flex items-center gap-2 p-3 border-t border-[var(--al-line)] mt-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={x.chatPlaceholder}
          maxLength={600}
          data-testid="qa-chat-input"
          className="flex-1 px-3 py-2 rounded-full border border-[var(--al-line)] bg-slate-50 text-xs focus:outline-none focus:ring-2 focus:ring-violet-300"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="qa-chat-send"
          className="h-8 w-8 rounded-full flex items-center justify-center text-white disabled:opacity-40 flex-shrink-0"
          style={{ background: "var(--al-grad)" }}
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </form>
    </div>
  );
};

/** Schmales Banner unter dem Hero — verlinkt das nächste Webinar auf /ai. */
export const WebinarBanner = () => {
  const webinar = useNextWebinar();
  const { lang } = useT();
  const x = TXT[lang] || TXT.de;
  if (!webinar) return null;
  return (
    <section data-testid="webinar-banner" className="relative">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-2 mb-2">
        <div
          className="rounded-2xl px-5 sm:px-7 py-4 text-white shadow-lg flex items-center gap-4 flex-wrap"
          style={{ background: "var(--al-grad)" }}
        >
          <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[11px] uppercase tracking-widest font-bold text-white/75">{x.bannerTag}</div>
            <div className="font-display font-bold text-base sm:text-lg leading-snug">
              {webinar.title}
              <span className="block sm:inline sm:ml-2 font-normal text-white/85 text-sm">
                {fmtWebinarDate(webinar.scheduled_at, lang)}
              </span>
            </div>
          </div>
          <Link
            to="/ai"
            onClick={() => track("webinar_banner_cta", { meta: { webinar_id: webinar.id } })}
            data-testid="webinar-banner-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-violet-700 font-display font-bold text-sm hover:bg-violet-50 transition-colors"
          >
            {x.bannerCta} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/** Floating-Widget unten rechts: Webinar-Eintragung + Q&A. */
export const WebinarPopup = () => {
  const webinar = useNextWebinar();
  const { lang } = useT();
  const x = TXT[lang] || TXT.de;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("webinar");

  // Auto-Open nach 20s — mit 24h-Cool-off nach manuellem Schliessen.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ts = Number(localStorage.getItem(POPUP_KEY));
    if (ts && Date.now() - ts < REDISPLAY_AFTER_MS) return;
    const timer = setTimeout(() => {
      setOpen(true);
      track("webinar_popup_shown", { meta: { reason: "timer_20s" } });
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    localStorage.setItem(POPUP_KEY, String(Date.now()));
    setOpen(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3" data-testid="webinar-popup-root">
      {open && (
        <div
          className="w-[330px] max-w-[calc(100vw-2.5rem)] rounded-2xl bg-white shadow-2xl border border-[var(--al-line)] overflow-hidden"
          data-testid="webinar-popup-panel"
        >
          <div className="px-4 py-3 text-white flex items-center justify-between" style={{ background: "var(--al-grad)" }}>
            <div className="flex items-center gap-2 font-display font-bold text-sm">
              <CalendarClock className="h-4 w-4" /> {x.popupTitle}
            </div>
            <button type="button" onClick={dismiss} className="p-1 rounded-full hover:bg-white/15" data-testid="webinar-popup-close">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex border-b border-[var(--al-line)] text-sm font-display font-semibold">
            <button
              type="button"
              onClick={() => setTab("webinar")}
              data-testid="webinar-popup-tab-webinar"
              className={`flex-1 py-2.5 ${tab === "webinar" ? "text-violet-700 border-b-2 border-violet-600 bg-violet-50/50" : "text-slate-500 hover:text-slate-700"}`}
            >
              {x.tabWebinar}
            </button>
            <button
              type="button"
              onClick={() => setTab("qa")}
              data-testid="webinar-popup-tab-qa"
              className={`flex-1 py-2.5 inline-flex items-center justify-center gap-1.5 ${tab === "qa" ? "text-violet-700 border-b-2 border-violet-600 bg-violet-50/50" : "text-slate-500 hover:text-slate-700"}`}
            >
              <MessageCircleQuestion className="h-4 w-4" /> {x.tabQa}
            </button>
          </div>

          {tab === "webinar" ? (
            <div className="p-4" data-testid="webinar-popup-webinar-tab">
              {webinar ? (
                <>
                  <div className="font-display font-bold text-slate-900 leading-snug">{webinar.title}</div>
                  <div className="mt-1 text-sm text-violet-700 font-semibold">{fmtWebinarDate(webinar.scheduled_at, lang)}</div>
                  {webinar.pitch_one_liner && (
                    <p className="mt-2 text-xs text-slate-600 leading-relaxed">{webinar.pitch_one_liner}</p>
                  )}
                  <Link
                    to="/ai"
                    onClick={() => track("webinar_popup_cta", { meta: { webinar_id: webinar.id } })}
                    data-testid="webinar-popup-cta"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white font-display font-bold text-sm hover:opacity-95 transition-opacity"
                    style={{ background: "var(--al-grad)" }}
                  >
                    {x.popupCta} <ArrowRight className="h-4 w-4" />
                  </Link>
                </>
              ) : (
                <Link
                  to="/ai"
                  data-testid="webinar-popup-cta"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white font-display font-bold text-sm"
                  style={{ background: "var(--al-grad)" }}
                >
                  {x.popupCta} <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          ) : (
            <QaChat x={x} />
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => (open ? dismiss() : (setOpen(true), track("webinar_popup_shown", { meta: { reason: "bubble_click" } })))}
        data-testid="webinar-popup-bubble"
        aria-label={x.bubbleLabel}
        className="inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full text-white font-display font-bold text-sm shadow-xl hover:scale-105 transition-transform"
        style={{ background: "var(--al-grad)" }}
      >
        <CalendarClock className="h-5 w-5" /> {x.bubbleLabel}
      </button>
    </div>
  );
};

/** Grosse Webinar-Sektion (Landingpage, unter "Über uns") mit Direkt-Eintragung. */
export const WebinarSection = () => {
  const webinar = useNextWebinar();
  const { lang } = useT();
  const x = TXT[lang] || TXT.de;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  if (!webinar) return null;
  const bullets = (webinar.bullets && webinar.bullets.length ? webinar.bullets : x.secBullets).slice(0, 4);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!name.trim()) { setErr(x.secNameReq); return; }
    setErr("");
    setLoading(true);
    const res = await submitLead(email, name, {
      source: "webinar_section",
      phone: phone ? phone.trim() : null,
    });
    setLoading(false);
    if (res) {
      track("webinar_section_submit", { meta: { webinar_id: webinar.id } });
      setDone(true);
    } else {
      setErr(x.secErr);
    }
  };

  return (
    <section id="webinar" data-testid="webinar-section" className="relative py-20 sm:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div
          className="relative overflow-hidden rounded-3xl text-white shadow-2xl"
          style={{
            background:
              "radial-gradient(120% 80% at 10% -10%, rgba(168,85,247,0.25) 0%, transparent 55%), " +
              "radial-gradient(80% 60% at 100% 100%, rgba(99,102,241,0.20) 0%, transparent 60%), " +
              "linear-gradient(160deg, #1B1340 0%, #221947 35%, #0E0A24 100%)",
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          />
          <div className="relative grid lg:grid-cols-2 gap-10 p-8 sm:p-12">
            {/* Links: Webinar-Infos */}
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/15 border border-violet-300/30 text-violet-100 text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5">
                <Sparkles className="h-3 w-3" /> {x.secTag}
              </span>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl leading-tight">
                {x.secTitle1}{" "}
                <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
                  {x.secTitle2}
                </span>
              </h2>
              <div className="mt-4 font-display font-bold text-lg text-white/95" data-testid="webinar-section-title">
                {webinar.title}
              </div>
              <div className="mt-1 inline-flex items-center gap-2 text-violet-200 font-semibold text-sm" data-testid="webinar-section-date">
                <CalendarClock className="h-4 w-4" /> {fmtWebinarDate(webinar.scheduled_at, lang)}
              </div>
              {webinar.pitch_one_liner && (
                <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-md">{webinar.pitch_one_liner}</p>
              )}
              <ul className="mt-5 space-y-2.5">
                {bullets.map((b, i) => (
                  <li key={typeof b === "string" ? b : i} className="flex items-start gap-2.5 text-sm text-white/85">
                    <CheckCircle2 className="h-5 w-5 text-emerald-300 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Rechts: Eintragungsformular */}
            <div className="rounded-2xl bg-white/[0.07] border border-white/15 backdrop-blur-md p-6 sm:p-7 self-center">
              {done ? (
                <div className="text-center py-8" data-testid="webinar-section-success">
                  <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto" />
                  <p className="mt-4 font-display font-bold text-lg">{x.secDone}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3" data-testid="webinar-section-form">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={x.secName}
                    required
                    data-testid="webinar-section-name"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={x.secEmail}
                    required
                    data-testid="webinar-section-email"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={x.secPhone}
                    data-testid="webinar-section-phone"
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-sm placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  {err && <p className="text-xs text-rose-300" data-testid="webinar-section-error">{err}</p>}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="webinar-section-submit"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-white font-display font-bold text-sm hover:opacity-95 transition-opacity disabled:opacity-60"
                    style={{ background: "var(--al-grad)" }}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{x.secSubmit} <ArrowRight className="h-4 w-4" /></>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
